import { useState } from "react";
import { useAuth } from "../../context/DataContext";
import { toast } from "react-toastify";
import "./Login.css";

// Inline SVG icons matching the prototype's icon set
const IconEye = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 12c2.6-4.6 6-7 9.5-7s6.9 2.4 9.5 7c-2.6 4.6-6 7-9.5 7S5.1 16.6 2.5 12z" />
        <circle cx="12" cy="12" r="2.6" />
    </svg>
);

const IconEyeOff = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3l18 18" />
        <path d="M10.7 6.2A10 10 0 0 1 12 6c4 0 7.4 2.3 9.5 6-.7 1.3-1.6 2.4-2.6 3.3" />
        <path d="M6.5 7.6C4.7 8.9 3.2 10.7 2.5 12c2.1 3.7 5.5 6 9.5 6 1.4 0 2.7-.3 3.9-.8" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
);

const IconArrowRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12h16" />
        <path d="M14 6l6 6-6 6" />
    </svg>
);

const IconGoogle = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 9.5h5.2c.2.7.3 1.5.3 2.5 0 3.6-2.4 6-5.5 6" />
    </svg>
);

const IconWarn = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l10 18H2z" />
        <path d="M12 10v5" />
        <circle cx="12" cy="18" r=".7" fill="currentColor" />
    </svg>
);

const Login = () => {
    const { signInWithGoogle, signInWithEmail } = useAuth();

    // State
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");

    // --- Handlers ---

    const handleEmailLogin = async (e) => {
        e.preventDefault();

        if (!email.trim() || !password.trim()) {
            setError("Please fill in all required fields.");
            return;
        }

        try {
            setLoading(true);
            setError("");
            // Call the Context function which handles Role & Navigation
            await signInWithEmail(email, password);
        } catch (err) {
            // The context throws an error message if it fails
            setError(err.message);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setLoading(true);
            setError("");
            await signInWithGoogle();
        } catch (err) {
            setError(err.message);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-screen">
            <div className="login-wrapper">
                {/* Logo Block */}
                <div className="login-logo-block">
                    <div className="login-logo-icon">A</div>
                    <div className="login-logo-title">APCS Scoring Platform</div>
                    <div className="login-logo-subtitle">jury portal · sign in</div>
                </div>

                {/* Form Card */}
                <form className="login-card" onSubmit={handleEmailLogin}>
                    {/* Error Banner */}
                    {error && (
                        <div className="login-error-banner">
                            <IconWarn />
                            <div>{error}</div>
                        </div>
                    )}

                    {/* Email Field */}
                    <label className="login-label" htmlFor="login-email">Email</label>
                    <input
                        id="login-email"
                        type="email"
                        className="login-input"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                        placeholder="you@school.edu"
                        autoFocus
                    />

                    {/* Password Field */}
                    <div className="login-password-row">
                        <span className="login-label" style={{ marginBottom: 0 }}>Password</span>
                        <button type="button" className="login-forgot-link" tabIndex={-1}>
                            Forgot password?
                        </button>
                    </div>
                    <div className="login-input-group">
                        <input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            className="login-iconbtn"
                            onClick={() => setShowPassword(s => !s)}
                            title={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <IconEyeOff /> : <IconEye />}
                        </button>
                    </div>

                    {/* Sign In Button */}
                    <button
                        type="submit"
                        className="login-btn primary"
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="login-spinner" />
                        ) : (
                            <>Sign in <IconArrowRight /></>
                        )}
                    </button>

                    {/* Divider */}
                    <div className="login-divider">
                        <div className="login-divider-line" />
                        <span className="login-divider-text">or</span>
                        <div className="login-divider-line" />
                    </div>

                    {/* Google Sign In */}
                    <button
                        type="button"
                        className="login-btn"
                        onClick={handleGoogleLogin}
                        disabled={loading}
                    >
                        <IconGoogle /> Continue with Google
                    </button>
                </form>

                {/* Footer */}
                <div className="login-footer">
                    By signing in you agree to our terms · privacy
                </div>
            </div>
        </div>
    );
};

export default Login;